import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {
    ArchiveBoxXMarkIcon,
    ArrowUpTrayIcon,
    InboxArrowDownIcon,
    UserGroupIcon,
    Bars3Icon,
    BellAlertIcon,
    BuildingStorefrontIcon,
    ChartBarIcon,
    CheckCircleIcon,
    ClipboardDocumentCheckIcon,
    CogIcon,
    MegaphoneIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    ExclamationTriangleIcon,
    FolderIcon,
    InformationCircleIcon,
    Squares2X2Icon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import {useStateContext} from "../../contexts/ContextProvider";
import {Link, Navigate, Outlet, useLocation} from "react-router-dom";
import Sidebar, {SidebarContext, SidebarItem, SidebarSection} from "./Sidebar";
import SidebarUserMenu from "./SidebarUserMenu";
import FacilitySwitcher from "./FacilitySwitcher";
import FacilitySwitcherModal from "./FacilitySwitcherModal";
import axiosClient from "../../api/axios-client";
import {usePermissions} from "../../contexts/PermissionContext";

import {FlowIcon} from "../../icons/globalIcons";
import PermissionErrorHandler from "../permissions/PermissionErrorHandler";
import {useDashboardAlerts} from "../../pages/alerts/api/useAlerts";


interface MenuItem {
    link: string;
    icon: JSX.Element;
    text: string;
    badgeCount?: number;
    exact?: boolean;
}

interface MenuSection {
    label?: string;
    items: MenuItem[];
}

export default function DefaultLayout() {

    const location = useLocation();

    const {token, user, setUser, isUserLoading, isFacilitySwitching, currentFacility, notifications, setNotifications} = useStateContext();
    const {hasPermission} = usePermissions();

    // État pour le modal de switch facility (mobile)
    const [mobileFacilitySwitcherModalOpen, setMobileFacilitySwitcherModalOpen] = useState(false);

    // État pour suivre l'expansion du sidebar
    const [sidebarExpanded, setSidebarExpanded] = useState(() => {
        const saved = localStorage.getItem('sidebarExpanded');
        return saved === 'true';
    });

    // Écouter les changements du localStorage pour synchroniser l'état
    useEffect(() => {
        const handleStorageChange = () => {
            const saved = localStorage.getItem('sidebarExpanded');
            setSidebarExpanded(saved === 'true');
        };

        // Vérifier périodiquement le localStorage (en cas de changement dans un autre composant)
        const interval = setInterval(handleStorageChange, 100);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        axiosClient.get('/user')
            .then(({data}) => {
                setUser(data);
            })
    }, []);

    if (!token) {
        return <Navigate to={"/login"}/>
    }

    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Alertes non résolues : badge sur l'entrée « Alertes » du menu
    const {data: dashboardAlerts} = useDashboardAlerts();
    const unresolvedAlertsCount = dashboardAlerts?.total ?? 0;

    // Menu structuré par sections, partagé par les barres mobile et desktop.
    // Ordre demandé par P&W (retours V1, story 9.1.5) : Établissements,
    // Campagnes, Alertes ; puis les matrices (gabarits communs à tous).
    const menuSections = useMemo((): MenuSection[] => {
        if (isUserLoading || user.id === 0) {
            return [];
        }

        const canView = (permission: string) => !permission || hasPermission(permission);
        const items = (...entries: (MenuItem | false | undefined)[]) => entries.filter(Boolean) as MenuItem[];

        return [
            {items: items(
                canView('campaigns.view') && {link: '/dashboard', icon: <Squares2X2Icon className="w-6 h-6"/>, text: 'Tableau de bord', exact: true},
            )},
            {label: 'Pilotage', items: items(
                user.is_super_admin && {link: '/facilities', icon: <BuildingStorefrontIcon className="w-6 h-6"/>, text: 'Établissements'},
                canView('campaigns.view') && {link: '/campaigns', icon: <MegaphoneIcon className="w-6 h-6"/>, text: 'Campagnes'},
                canView('campaigns.view') && {link: '/alerts', icon: <BellAlertIcon className="w-6 h-6"/>, text: 'Alertes', badgeCount: unresolvedAlertsCount},
            )},
            {label: 'Paramétrage matrices', items: items(
                canView('email_templates.view') && {link: '/email-templates', icon: <EnvelopeIcon className="w-6 h-6"/>, text: "Templates d'email"},
                canView('surveys.view') && {link: '/surveys', icon: <ClipboardDocumentCheckIcon className="w-6 h-6"/>, text: 'Formulaires'},
                canView('reports.view') && {link: '/documents', icon: <DocumentTextIcon className="w-6 h-6"/>, text: 'Rapports'},
            )},
            {label: 'Général', items: items(
                canView('reports.view') && {link: '/files', icon: <FolderIcon className="w-6 h-6"/>, text: 'Fichiers'},
                canView('settings.view') && {link: '/settings', icon: <CogIcon className="w-6 h-6"/>, text: 'Paramètres'},
            )},
        ].filter(section => section.items.length > 0);
    }, [hasPermission, isUserLoading, user.id, user.is_super_admin, unresolvedAlertsCount]);

    // Menu Super Admin (uniquement pour les super admins)
    const superAdminNavigation = [
        {link: '/super-admin/analytics', icon: <ChartBarIcon className="w-6 h-6"/>, text: 'Analytics'},
        {link: '/super-admin/config', icon: <CogIcon className="w-6 h-6"/>, text: 'Configuration'},
        {link: '/super-admin/retention', icon: <ArchiveBoxXMarkIcon className="w-6 h-6"/>, text: 'Conservation RGPD'},
        {link: '/super-admin/campaign-requests', icon: <InboxArrowDownIcon className="w-6 h-6"/>, text: 'Demandes de campagne'},
        {link: '/super-admin/imports', icon: <ArrowUpTrayIcon className="w-6 h-6"/>, text: 'Imports'},
        // Le propriétaire de la plateforme seul choisit qui entre dans l'équipe.
        user.is_platform_owner && {link: '/super-admin/team', icon: <UserGroupIcon className="w-6 h-6"/>, text: 'Équipe P&W'},
    ].filter(Boolean) as MenuItem[];

    const allSections: MenuSection[] = user.is_super_admin
        ? [...menuSections, {label: 'Super Admin', items: superAdminNavigation}]
        : menuSections;

    const renderSections = (onItemClick?: () => void) => allSections.map((section, index) => (
        <Fragment key={section.label ?? index}>
            {section.label && <SidebarSection label={section.label}/>}
            {section.items.map(item => (
                <SidebarItem
                    key={item.link}
                    link={item.link}
                    icon={item.icon}
                    text={item.text}
                    badgeCount={item.badgeCount}
                    active={item.exact ? location.pathname === item.link : location.pathname.startsWith(item.link)}
                    onClick={onItemClick}
                />
            ))}
        </Fragment>
    ));

    return (
        <div>
            <PermissionErrorHandler />
            <div className="h-full">

                {/*##################################################*/}
                {/* Début - Barre latérale pour la version mobile*/}
                {/*##################################################*/}
                <Transition.Root show={sidebarOpen} as={Fragment}>
                    <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
                        <Transition.Child
                            as={Fragment}
                            enter="transition-opacity ease-linear duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="transition-opacity ease-linear duration-300"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-gray-900/80"/>
                        </Transition.Child>
                        <div className="fixed inset-0 flex">
                            <Transition.Child
                                as={Fragment}
                                enter="transition ease-in-out duration-300 transform"
                                enterFrom="-translate-x-full"
                                enterTo="translate-x-0"
                                leave="transition ease-in-out duration-300 transform"
                                leaveFrom="translate-x-0"
                                leaveTo="-translate-x-full"
                            >
                                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                                    <Transition.Child
                                        as={Fragment}
                                        enter="ease-in-out duration-300"
                                        enterFrom="opacity-0"
                                        enterTo="opacity-100"
                                        leave="ease-in-out duration-300"
                                        leaveFrom="opacity-100"
                                        leaveTo="opacity-0"
                                    >
                                        <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                                            <button type="button" className="-m-2.5 p-2.5"
                                                    onClick={() => setSidebarOpen(false)}>
                                                <span className="sr-only">Fermer la barre latérale</span>
                                                <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true"/>
                                            </button>
                                        </div>
                                    </Transition.Child>
                                    {/* Barre latérale mobile : mêmes sections que le desktop */}
                                    <div className="flex grow flex-col overflow-y-auto bg-[#FAFAFA]">
                                        {/* Logo */}
                                        <div className="flex h-14 shrink-0 items-center px-4">
                                            <img
                                                className="h-6 w-auto"
                                                src="/images/logo-peace-and-work.png"
                                                alt="Peace&Work Logo"
                                            />
                                        </div>

                                        {/* Facility Switcher */}
                                        <FacilitySwitcher
                                            isCompactMode={false}
                                            onClick={() => {
                                                setSidebarOpen(false);
                                                setMobileFacilitySwitcherModalOpen(true);
                                            }}
                                        />

                                        {/* Menu items */}
                                        <SidebarContext.Provider value={{expanded: true, setExpanded: () => {}}}>
                                            <div className="flex-1 overflow-y-auto px-2 pt-2">
                                                <ul className="flex flex-col">

                                                    {renderSections(() => setSidebarOpen(false))}
                                                </ul>
                                            </div>

                                            {/* Bottom section */}
                                            <div className="flex-none pb-3">
                                                <div className="w-full border-t border-gray-200 mt-2 mb-3"></div>
                                                <SidebarUserMenu
                                                    expanded={true}
                                                    facility={currentFacility}
                                                />
                                            </div>
                                        </SidebarContext.Provider>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </Dialog>
                </Transition.Root>
                {/*##################################################*/}
                {/* Fin - Barre latérale pour la version mobile*/}
                {/*##################################################*/}
                {/*##################################################*/}

                {/*##################################################*/}
                {/* Début - Barre latérale pour la version desktop */}
                {/*##################################################*/}
                <Sidebar>
                    {renderSections()}
                </Sidebar>
                {/*##################################################*/}
                {/* Fin - Barre latérale pour la version desktop */}
                {/*##################################################*/}


                {/*##################################################*/}
                {/* Début - En-tête de la version mobile */}
                {/*##################################################*/}
                <div
                    className="fixed w-full min-h-16 top-0 z-30 flex items-center justify-between gap-x-6 px-4 py-2 sm:px-6 lg:hidden pointer-events-none">
                    <button type="button" className="-m-2.5 p-2.5 lg:hidden pointer-events-auto"
                            onClick={() => setSidebarOpen(true)}>
                        <span className="sr-only">Ouvrir la barre latérale</span>
                        <Bars3Icon className="h-6 w-6" aria-hidden="true"/>
                    </button>
                </div>
                {/*##################################################*/}
                {/* Fin - En-tête de la version mobile */}
                {/*##################################################*/}

                <main className={`h-full transition-all duration-300 ease-out ${sidebarExpanded ? 'lg:ml-60' : 'lg:ml-[62px]'}`}>
                    <div className="h-full">

                        {/* Garde-fou de la phase de test : rappelé en permanence,
                            pour que personne ne croie un mail parti pour de vrai. */}
                        {user.mail_test_redirect && (
                            <div className="flex flex-wrap items-center justify-center gap-x-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-sm text-amber-900">
                                <span>
                                    <strong>Mode test</strong> : tous les emails partent à {user.mail_test_redirect}, aucun à son vrai destinataire.
                                </span>
                                {user.is_super_admin && (
                                    <Link to="/super-admin/config/mail-test-mode" className="font-medium underline underline-offset-2">
                                        Régler
                                    </Link>
                                )}
                            </div>
                        )}

                        <Outlet/>

                    </div>
                </main>

                {/* Overlay de chargement lors du changement d'établissement */}
                {isFacilitySwitching && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/80 backdrop-blur-sm">
                        <div className="flex flex-col items-center gap-3">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-main-color"></div>
                            <p className="text-sm font-medium text-gray-600">Changement d'établissement...</p>
                        </div>
                    </div>
                )}

                {/* Global notification toast */}
                <div
                    aria-live="assertive"
                    className="pointer-events-none fixed inset-x-0 top-0 flex items-start justify-center px-4 py-4 z-50"
                >
                    <div className="flex w-full flex-col items-center space-y-4">
                        <Transition
                            show={!!notifications}
                            as={Fragment}
                            enter="transform ease-out duration-300 transition"
                            enterFrom="translate-y-2 opacity-0"
                            enterTo="translate-y-0 opacity-100"
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                                <div className="p-4">
                                    <div className="flex items-start">
                                        <div className="flex-shrink-0">
                                            {notifications?.success && (
                                                <CheckCircleIcon className="h-6 w-6 text-green-400" aria-hidden="true" />
                                            )}
                                            {notifications?.error && (
                                                <XMarkIcon className="h-6 w-6 text-red-400" aria-hidden="true" />
                                            )}
                                            {notifications?.warning && (
                                                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                                            )}
                                            {notifications?.info && (
                                                <InformationCircleIcon className="h-6 w-6 text-blue-400" aria-hidden="true" />
                                            )}
                                        </div>
                                        <div className="ml-3 w-0 flex-1 pt-0.5">
                                            <p className="text-sm text-gray-500">
                                                {notifications?.success}
                                                {notifications?.error}
                                                {notifications?.warning}
                                                {notifications?.info}
                                            </p>
                                        </div>
                                        <div className="ml-4 flex flex-shrink-0">
                                            <button
                                                type="button"
                                                className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                                onClick={() => setNotifications(null)}
                                            >
                                                <span className="sr-only">Fermer</span>
                                                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Transition>
                    </div>
                </div>

            </div>

            {/* Modal de switch facility (mobile) */}
            <FacilitySwitcherModal
                open={mobileFacilitySwitcherModalOpen}
                setOpen={setMobileFacilitySwitcherModalOpen}
            />
        </div>
    )
}
